import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlay, 
  FiPause, 
  FiVolume2, 
  FiVolumeX,
  FiMaximize,
  FiMinimize,
  FiArrowLeft,
  FiRefreshCw
} from 'react-icons/fi';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { podcastsAPI, Podcast } from '../services/api';
import './PodcastPlayer.css';
import './Dashboard.css';

interface PodcastPlayerProps {
  onLogout?: () => void;
  userEmail?: string;
}

const PodcastPlayer: React.FC<PodcastPlayerProps> = ({ onLogout, userEmail }) => {
  const { podcastId } = useParams<{ podcastId: string }>();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [podcast, setPodcast] = useState<Podcast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewsIncrementedRef = useRef<boolean>(false);
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const incrementingViewsRef = useRef<boolean>(false); // Prevent concurrent increments
  const incrementPromiseRef = useRef<Promise<void> | null>(null); // Store the increment promise to prevent duplicate calls
  const progressUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check if views were incremented in this session
  const hasIncrementedViewsInSession = (podcastId: string): boolean => {
    const key = `podcast_views_${podcastId}`;
    return sessionStorage.getItem(key) === 'true';
  };

  // Helper function to mark views as incremented in this session
  const markViewsIncrementedInSession = (podcastId: string): void => {
    const key = `podcast_views_${podcastId}`;
    sessionStorage.setItem(key, 'true');
  };

  useEffect(() => {
    if (!podcastId) return;
    
    // Reset refs when podcastId changes
    viewsIncrementedRef.current = false;
    incrementingViewsRef.current = false;
    incrementPromiseRef.current = null;
    
    fetchPodcast();
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [podcastId]);

  // Keyboard event handler for Space key
  useEffect(() => {
    const handleKeyPress = async (e: KeyboardEvent) => {
      // Prevent default if Space is pressed and not in an input/textarea
      const target = e.target as HTMLElement;
      if (e.code === 'Space' && target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        const video = videoRef.current;
        if (!video) return;

        try {
          if (isPlaying) {
            video.pause();
          } else {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              await playPromise;
            }
          }
        } catch (err: any) {
          console.error('[PodcastPlayer] Error in keyboard toggle:', err);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      console.log('[PodcastPlayer] Video ref is null');
      return;
    }

    if (!podcast) {
      console.log('[PodcastPlayer] Podcast is null, skipping video setup');
      return;
    }

    console.log('[PodcastPlayer] Setting up video event listeners');
    console.log('[PodcastPlayer] Podcast data:', {
      id: podcast.id,
      name: podcast.name,
      video_stream_url: podcast.video_stream_url ? `${podcast.video_stream_url.substring(0, 80)}...` : 'null',
      video_garage_key: podcast.video_garage_key,
      video_mime_type: podcast.video_mime_type
    });
    
    // Set video source - use stream endpoint through Django to avoid CORS issues
    // The presigned URL from Garage service has CORS issues, so we use Django's stream endpoint
    const streamUrl = podcastsAPI.getStreamURL(podcast.id);
    console.log('[PodcastPlayer] Using Django stream endpoint to avoid CORS issues');
    console.log('[PodcastPlayer] Stream URL:', streamUrl.substring(0, 100));
    video.src = streamUrl;
    video.load(); // Force reload to ensure video loads
    console.log('[PodcastPlayer] Video src set and load() called, readyState:', video.readyState);

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      
      // Update watch progress (debounced - every 5 seconds)
      if (progressUpdateTimeoutRef.current) {
        clearTimeout(progressUpdateTimeoutRef.current);
      }
      
      progressUpdateTimeoutRef.current = setTimeout(() => {
        if (podcast && video.currentTime > 0) {
          console.log(`[PodcastPlayer] Updating progress for podcast ${podcast.id}: ${video.currentTime} seconds`);
          // Include duration if available and podcast doesn't have it
          const durationToSend = (video.duration > 0 && !podcast.duration) ? video.duration : undefined;
          podcastsAPI.updateWatchProgress(podcast.id, video.currentTime, durationToSend)
            .then((result) => {
              console.log(`[PodcastPlayer] Progress updated successfully:`, result);
              // Refresh podcast data if duration was updated
              if (durationToSend) {
                podcastsAPI.getPodcast(podcast.id).then(updatedPodcast => {
                  setPodcast(updatedPodcast);
                });
              }
            })
            .catch(err => {
              console.error('[PodcastPlayer] Error updating watch progress:', err);
            });
        }
      }, 5000); // Update every 5 seconds
    };

    const handleLoadedMetadata = () => {
      console.log('[PodcastPlayer] Metadata loaded:', {
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        readyState: video.readyState
      });
      setDuration(video.duration);
      
      // Update podcast duration in backend if it's missing or different
      // We'll send it with the next progress update
      if (podcast && video.duration > 0 && (!podcast.duration || Math.abs(podcast.duration - video.duration) > 1)) {
        console.log(`[PodcastPlayer] Will update podcast duration: ${video.duration} seconds (current: ${podcast.duration})`);
        // Duration will be updated with the next progress update
      }
    };

    const handleLoadStart = () => {
      console.log('[PodcastPlayer] Load start');
      setBuffering(true);
    };

    const handleLoadedData = () => {
      console.log('[PodcastPlayer] Data loaded');
    };

    const handleCanPlay = () => {
      console.log('[PodcastPlayer] Can play');
      setBuffering(false);
    };

    const handleCanPlayThrough = () => {
      console.log('[PodcastPlayer] Can play through');
      setBuffering(false);
    };

    const handlePlay = () => {
      console.log('[PodcastPlayer] Play event');
      setIsPlaying(true);
      setBuffering(false);
    };

    const handlePause = () => {
      console.log('[PodcastPlayer] Pause event');
      setIsPlaying(false);
    };

    const handleWaiting = () => {
      console.log('[PodcastPlayer] Waiting for data');
      setBuffering(true);
    };

    const handleStalled = () => {
      console.log('[PodcastPlayer] Stalled');
      setBuffering(true);
    };

    const handleError = (e: Event) => {
      const videoError = video.error;
      console.error('[PodcastPlayer] Video error:', {
        error: videoError,
        code: videoError?.code,
        message: videoError?.message,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src
      });
      setError(`Video error: ${videoError?.message || 'Unknown error'}`);
      setBuffering(false);
    };

    const handleEnded = () => {
      console.log('[PodcastPlayer] Video ended');
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('error', handleError);
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      console.log('[PodcastPlayer] Cleaning up video event listeners');
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('error', handleError);
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [podcast]);

  const fetchPodcast = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!podcastId) {
        throw new Error('Podcast ID is required');
      }
      console.log('[PodcastPlayer] Fetching podcast with ID:', podcastId);
      
      // Fetch podcast data without incrementing views
      const data = await podcastsAPI.getPodcast(parseInt(podcastId));
      console.log('[PodcastPlayer] Podcast data received:', {
        id: data.id,
        name: data.name,
        video_stream_url: data.video_stream_url,
        video_garage_key: data.video_garage_key,
        video_mime_type: data.video_mime_type,
        watch_progress: data.watch_progress
      });
      setPodcast(data);
      
      // If user has watch progress, restore it
      if (data.watch_progress && data.watch_progress.progress_seconds > 0 && videoRef.current) {
        // Only restore if progress is less than 90% (don't auto-resume near the end)
        if (data.watch_progress.progress_percentage < 90) {
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.currentTime = data.watch_progress!.progress_seconds;
            }
          }, 500);
        }
      }
      
      // Increment views only once per session when podcast is first loaded
      // Use sessionStorage to prevent increment on refresh, but allow increment on new session
      // Also use refs and promise to prevent double increment in StrictMode
      const hasIncremented = hasIncrementedViewsInSession(podcastId);
      
      // Only increment if:
      // 1. Not already incremented in this session (sessionStorage check)
      // 2. Not already incremented in this component instance (ref check)
      // 3. Not currently incrementing (prevent race conditions)
      // 4. No pending increment promise (prevent duplicate calls in StrictMode)
      if (!hasIncremented && !viewsIncrementedRef.current && !incrementingViewsRef.current && !incrementPromiseRef.current) {
        incrementingViewsRef.current = true; // Set flag before async call to prevent concurrent calls
        
        // Create and store the increment promise to prevent duplicate calls
        const incrementPromise = (async () => {
          try {
            console.log('[PodcastPlayer] Incrementing views for podcast:', podcastId);
            await podcastsAPI.incrementViews(parseInt(podcastId));
            
            // Mark as incremented in both ref and sessionStorage
            viewsIncrementedRef.current = true;
            markViewsIncrementedInSession(podcastId);
            
            // Update views count in state
            setPodcast(prev => prev ? { ...prev, views_count: (prev.views_count || 0) + 1 } : null);
            console.log('[PodcastPlayer] Views incremented successfully');
          } catch (err) {
            console.error('[PodcastPlayer] Error incrementing views:', err);
            incrementingViewsRef.current = false; // Reset flag on error so it can be retried
            incrementPromiseRef.current = null; // Clear promise on error
            // Don't fail the whole request if view increment fails
          } finally {
            incrementPromiseRef.current = null; // Clear promise when done
          }
        })();
        
        incrementPromiseRef.current = incrementPromise;
      } else {
        console.log('[PodcastPlayer] Views already incremented, skipping:', {
          viewsIncrementedRef: viewsIncrementedRef.current,
          hasIncrementedInSession: hasIncremented,
          incrementing: incrementingViewsRef.current,
          hasPendingPromise: !!incrementPromiseRef.current,
          podcastId
        });
      }
    } catch (err: any) {
      console.error('[PodcastPlayer] Error fetching podcast:', err);
      setError(err.message || 'Failed to load podcast');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayPause = async () => {
    const video = videoRef.current;
    if (!video) {
      console.error('[PodcastPlayer] Video ref is null in togglePlayPause');
      return;
    }

    console.log('[PodcastPlayer] togglePlayPause called:', {
      isPlaying,
      readyState: video.readyState,
      networkState: video.networkState,
      src: video.src,
      error: video.error
    });

    try {
      if (isPlaying) {
        console.log('[PodcastPlayer] Pausing video');
        video.pause();
      } else {
        console.log('[PodcastPlayer] Attempting to play video');
        const playPromise = video.play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log('[PodcastPlayer] Video play() succeeded');
        }
      }
    } catch (err: any) {
      console.error('[PodcastPlayer] Error in togglePlayPause:', err);
      setError(`Playback error: ${err.message || 'Unknown error'}`);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!isFullscreen) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleMouseLeave = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      setShowControls(false);
    }
  };

  if (loading) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userEmail}
            organization="NAATI"
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="loading-container">
              {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
              <p>Loading podcast...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !podcast) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userEmail}
            organization="NAATI"
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="error-container">
              <p>{error || 'Podcast not found'}</p>
              <button onClick={() => navigate('/material/podcast')} className="back-btn">
                {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 16 })}
                <span>Back to Podcasts</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userEmail}
          organization="NAATI"
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="podcast-player-container">
            <button onClick={() => navigate('/material/podcast')} className="back-button">
              {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 20 })}
              <span>Back to Podcasts</span>
            </button>

            <div className="video-wrapper">
              <div
                className="video-container"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <video
                  ref={videoRef}
                  key={`video-${podcast.id}-${podcast.video_stream_url ? 'presigned' : 'stream'}`}
                  className="video-player"
                  playsInline
                  preload="auto"
                  crossOrigin="anonymous"
                  onLoadStart={() => {
                    console.log('[PodcastPlayer] Video onLoadStart');
                    const video = videoRef.current;
                    if (video) {
                      console.log('[PodcastPlayer] Video src:', video.src.substring(0, 100));
                      console.log('[PodcastPlayer] Video networkState:', video.networkState);
                    }
                  }}
                  onLoadedMetadata={() => {
                    console.log('[PodcastPlayer] Video onLoadedMetadata');
                    const video = videoRef.current;
                    if (video) {
                      console.log('[PodcastPlayer] Video metadata loaded:', {
                        duration: video.duration,
                        videoWidth: video.videoWidth,
                        videoHeight: video.videoHeight,
                        readyState: video.readyState,
                        networkState: video.networkState
                      });
                    }
                  }}
                  onCanPlay={() => {
                    console.log('[PodcastPlayer] Video onCanPlay - ready to play');
                    const video = videoRef.current;
                    if (video) {
                      console.log('[PodcastPlayer] Video can play, readyState:', video.readyState);
                    }
                  }}
                  onError={(e) => {
                    const video = e.currentTarget;
                    const error = video.error;
                    console.error('[PodcastPlayer] Video onError:', {
                      error: error,
                      code: error?.code,
                      message: error?.message,
                      networkState: video.networkState,
                      readyState: video.readyState,
                      src: video.src.substring(0, 150)
                    });
                    
                    // Error code meanings:
                    // 1 = MEDIA_ERR_ABORTED
                    // 2 = MEDIA_ERR_NETWORK
                    // 3 = MEDIA_ERR_DECODE
                    // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
                    const errorMessages: { [key: number]: string } = {
                      1: 'Video loading aborted',
                      2: 'Network error while loading video',
                      3: 'Video decoding error',
                      4: 'Video format not supported'
                    };
                    const errorMsg = errorMessages[error?.code || 0] || error?.message || 'Unknown video error';
                    setError(`Video playback error: ${errorMsg}`);
                  }}
                />

                {buffering && (
                  <div className="buffering-indicator">
                    {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                  </div>
                )}

                <motion.div
                  className={`video-controls ${showControls ? 'visible' : 'hidden'}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showControls ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Progress Bar */}
                  <div className="progress-bar-container" onClick={handleSeek}>
                    <div className="progress-bar">
                      <div
                        className="progress-filled"
                        style={{ width: `${progressPercentage}%` }}
                      />
                      <div
                        className="progress-handle"
                        style={{ left: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="controls-bar">
                    <div className="controls-left">
                      {/* Play/Pause Button */}
                      <button
                        className="control-btn play-pause-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlayPause();
                        }}
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                      >
                        {isPlaying
                          ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 22 })
                          : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 22 })
                        }
                      </button>

                      {/* Time Display */}
                      <div className="time-display">
                        <span>{formatTime(currentTime)}</span>
                        <span style={{ opacity: 0.6 }}>/</span>
                        <span style={{ opacity: 0.8 }}>{formatTime(duration)}</span>
                      </div>

                      {/* Volume Control */}
                      <div 
                        className="volume-control-wrapper"
                        ref={volumeControlRef}
                        onMouseEnter={() => setShowVolumeSlider(true)}
                        onMouseLeave={() => setShowVolumeSlider(false)}
                      >
                        <button
                          className="control-btn volume-btn-circle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMute();
                          }}
                          aria-label={isMuted ? 'Unmute' : 'Mute'}
                        >
                          {isMuted
                            ? React.createElement(FiVolumeX as React.ComponentType<{ size?: number }>, { size: 22 })
                            : React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 22 })
                          }
                        </button>
                        <AnimatePresence>
                          {showVolumeSlider && (
                            <motion.div
                              className="volume-slider-popup"
                              initial={{ opacity: 0, scale: 0.9, y: 5 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.9, y: 5 }}
                              transition={{ duration: 0.15 }}
                            >
                              <div className="volume-slider-container">
                                <div className="volume-slider-wrapper">
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={isMuted ? 0 : volume}
                                    onChange={handleVolumeChange}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    className="volume-slider-horizontal"
                                    aria-label="Volume"
                                  />
                                </div>
                                <div className="volume-value-display">
                                  {Math.round((isMuted ? 0 : volume) * 100)}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div className="controls-right">
                      {/* Fullscreen Button */}
                      <button
                        className="control-btn fullscreen-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFullscreen();
                        }}
                        aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                      >
                        {isFullscreen
                          ? React.createElement(FiMinimize as React.ComponentType<{ size?: number }>, { size: 18 })
                          : React.createElement(FiMaximize as React.ComponentType<{ size?: number }>, { size: 18 })
                        }
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Video Info */}
            <div className="video-info">
              <h1 className="video-title">{podcast.name}</h1>
              {podcast.description && (
                <p className="video-description">{podcast.description}</p>
              )}
              <div className="video-meta">
                <span className="video-views">{podcast.views_count.toLocaleString()} views</span>
                {podcast.created_at && (
                  <span className="video-date">
                    {new Date(podcast.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodcastPlayer;

